import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserPlus, Key, EnvelopeSimple, ShieldCheck, ClockCounterClockwise } from '@phosphor-icons/react';
import { useAuth } from '../../context/AuthContext';
import { UsersTab } from './UsersTab';
import { InvitationsTab } from './InvitationsTab';
import { PendingTab } from './PendingTab';
import { LoginHistoryTab } from './LoginHistoryTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AccessManagementPage = () => {
  const { user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [pending, setPending] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [uRes, iRes, pRes, lRes] = await Promise.all([
        axios.get(`${API_URL}/api/access/users`, { withCredentials: true }),
        axios.get(`${API_URL}/api/access/invitations`, { withCredentials: true }),
        axios.get(`${API_URL}/api/auth/pending-registrations`, { withCredentials: true }),
        axios.get(`${API_URL}/api/access/login-history?limit=500`, { withCredentials: true }),
      ]);
      setUsers(uRes.data);
      setInvitations(iRes.data);
      setPending(pRes.data);
      setLoginHistory(lRes.data || []);
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleRevoke = async (id, name) => {
    if (!window.confirm(`Revoquer l'acces de ${name} ?`)) return;
    try { await axios.put(`${API_URL}/api/access/users/${id}/revoke`, {}, { withCredentials: true }); toast.success('Acces revoque'); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const handleReactivate = async (id) => {
    try { await axios.put(`${API_URL}/api/access/users/${id}/reactivate`, {}, { withCredentials: true }); toast.success('Acces reactive'); fetchAll(); }
    catch { toast.error('Erreur'); }
  };
  const handleDeleteUser = async (id, name) => {
    if (!window.confirm(`Supprimer DEFINITIVEMENT le compte de ${name} ? Cette action est irreversible.`)) return;
    try { await axios.delete(`${API_URL}/api/access/users/${id}`, { withCredentials: true }); toast.success('Compte supprime'); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const handleRoleChange = async (id, name, role) => {
    const label = role === 'admin' ? 'Administrateur' : 'Salarie';
    if (!window.confirm(`Definir ${name} comme ${label} ?`)) return;
    try {
      await axios.put(`${API_URL}/api/access/users/${id}/role`, { role }, { withCredentials: true });
      toast.success(`Role mis a jour : ${label}`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const handleCreateInvite = async (form) => {
    try {
      const res = await axios.post(`${API_URL}/api/access/invitations`, { ...form, origin: window.location.origin }, { withCredentials: true });
      if (res.data?.email_sent) {
        toast.success(`Invitation envoyee a ${form.email}`);
      } else {
        toast.warning(res.data?.email_error ? `Invitation creee mais email non envoye: ${res.data.email_error}` : 'Invitation creee - copiez le lien');
      }
      fetchAll();
      return res.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
      return null;
    }
  };
  const handleResendEmail = async (id, email) => {
    try {
      await axios.post(`${API_URL}/api/access/invitations/${id}/send`, { origin: window.location.origin }, { withCredentials: true });
      toast.success(`Email envoye a ${email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Echec envoi');
    }
  };
  const handleDeleteInvite = async (id) => {
    if (!window.confirm('Supprimer cette invitation ?')) return;
    try { await axios.delete(`${API_URL}/api/access/invitations/${id}`, { withCredentials: true }); toast.success('Invitation supprimee'); fetchAll(); }
    catch { toast.error('Erreur'); }
  };
  const handleApprove = async (id, name) => {
    try { await axios.put(`${API_URL}/api/auth/approve/${id}`, {}, { withCredentials: true }); toast.success(`${name} approuve`); fetchAll(); }
    catch { toast.error('Erreur'); }
  };
  const handleReject = async (id, name) => {
    if (!window.confirm(`Refuser l'inscription de ${name} ?`)) return;
    try { await axios.delete(`${API_URL}/api/auth/reject/${id}`, { withCredentials: true }); toast.success('Inscription refusee'); fetchAll(); }
    catch { toast.error('Erreur'); }
  };

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  const handleNotifyMissingPhoto = async () => {
    if (!window.confirm('Envoyer une notification a tous les salaries sans photo de profil ?')) return;
    try {
      const res = await axios.post(`${API_URL}/api/notifications/notify-missing-photo`, {}, { withCredentials: true });
      toast.success(res.data.message || 'Notifications envoyees');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const tabs = [
    { key: 'users', label: 'Utilisateurs', icon: ShieldCheck, count: users.length },
    { key: 'invitations', label: 'Invitations', icon: EnvelopeSimple, count: invitations.length },
    { key: 'pending', label: 'Inscriptions en attente', icon: UserPlus, count: pending.length },
    { key: 'history', label: 'Historique connexions', icon: ClockCounterClockwise, count: loginHistory.length },
  ];

  return (
    <div data-testid="access-page">
      <div className="page-header" style={{ marginBottom: '18px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Key size={26} weight="bold" /> Gestion des acces
          </h1>
          <p style={{ color: '#5E5F66', marginTop: '4px', fontSize: '0.9rem' }}>
            {users.length} utilisateur(s) - {invitations.length} invitation(s) - {pending.length} inscription(s) en attente
          </p>
        </div>
        <button className="btn btn-secondary" onClick={handleNotifyMissingPhoto} data-testid="notify-missing-photo-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <EnvelopeSimple size={16} /> Relancer photo de profil manquante
        </button>
      </div>

      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #E2E2E6', marginBottom: '20px', overflowX: 'auto' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} data-testid={`access-tab-${t.key}`}
            style={{ padding: '10px 18px', border: 'none', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '600', backgroundColor: 'transparent', borderBottom: activeTab === t.key ? '2px solid #002FA7' : '2px solid transparent', color: activeTab === t.key ? '#002FA7' : '#5E5F66', marginBottom: '-2px', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
            <t.icon size={16} /> {t.label} ({t.count})
          </button>
        ))}
      </div>

      {activeTab === 'users' && <UsersTab users={users} onRevoke={handleRevoke} onReactivate={handleReactivate} onDelete={handleDeleteUser} onRoleChange={handleRoleChange} currentUserId={currentUser?.id} />}
      {activeTab === 'invitations' && <InvitationsTab invitations={invitations} onCreate={handleCreateInvite} onDelete={handleDeleteInvite} onResend={handleResendEmail} />}
      {activeTab === 'pending' && <PendingTab pending={pending} onApprove={handleApprove} onReject={handleReject} />}
      {activeTab === 'history' && <LoginHistoryTab loginHistory={loginHistory} />}
    </div>
  );
};

export default AccessManagementPage;
